/**
 * A fixed title and fingerprint for a family of failures whose own messages are unbounded - backend
 * error text, socket URLs, wire payloads. Without one, every distinct string files its own issue, so
 * a single recurring fault reads as hundreds of one-off events and nothing can be ranked by volume.
 *
 * Both halves are needed. The fingerprint is what groups, since these carry a stack and Sentry groups
 * on stack frames whatever the message says. The title is what stops the one issue being named after
 * whichever event happened to arrive last. The real message survives on the event as a context.
 *
 * Neither is applied where it is requested: `captureException` only records the intent, and
 * `applyGroupAs` carries it out from `beforeSend`. See that function for why the timing matters.
 */
export type GroupAs = {
  title: string;
  fingerprint: string[];
};

/**
 * Every REST and GraphQL failure the default handlers report. The endpoint or query name is already
 * on `prophecy.error.http` / `prophecy.error.gql` and the status on `prophecy.network.status`, so the
 * breakdown that used to be spread over thousands of issues is now a tag distribution inside one.
 */
export const API_FAILURE: GroupAs = {
  title: 'API request failed',
  fingerprint: ['api-failure']
};
